import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { BanknotesIcon, ArrowLeftIcon, CurrencyDollarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import { parseCurrency, applyCurrencyMask, formatCurrencyInput } from '../utils/currency.ts';
import FormPage from '../components/ui/FormPage.tsx';

interface ReceivableFormData {
  customerName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category: 'real_estate' | 'vehicle' | 'mutuos' | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
}

interface Receivable extends ReceivableFormData {
  id: string;
  createdAt: string;
  updatedAt: string;
}

const EditReceivable: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ReceivableFormData>({
    customerName: '',
    description: '',
    originalAmount: 0,
    currentAmount: 0,
    interestRate: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'active',
    category: 'other',
    paymentMethod: '',
    installments: 1,
    currentInstallment: 1,
  });

  const [originalAmountDisplay, setOriginalAmountDisplay] = useState('');
  const [currentAmountDisplay, setCurrentAmountDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: receivable, isLoading: isLoadingItem } = useQuery<Receivable>(
    ['receivable', id],
    async () => {
      const response = await api.get(`/receivables/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  useEffect(() => {
    if (receivable) {
      setFormData({
        customerName: receivable.customerName || '',
        description: receivable.description || '',
        originalAmount: receivable.originalAmount || 0,
        currentAmount: receivable.currentAmount || 0,
        interestRate: receivable.interestRate || 0,
        dueDate: receivable.dueDate || new Date().toISOString().split('T')[0],
        status: receivable.status,
        category: receivable.category,
        paymentMethod: receivable.paymentMethod || '',
        installments: receivable.installments || 1,
        currentInstallment: receivable.currentInstallment || 1,
      });
      setOriginalAmountDisplay(formatCurrencyInput(receivable.originalAmount));
      setCurrentAmountDisplay(formatCurrencyInput(receivable.currentAmount));
    }
  }, [receivable]);

  const updateMutation = useMutation(
    async (data: ReceivableFormData) => {
      const payload = {
        customerName: data.customerName,
        description: data.description,
        originalAmount: data.originalAmount,
        currentAmount: data.currentAmount,
        interestRate: data.interestRate,
        dueDate: data.dueDate,
        status: data.status,
        category: data.category,
        paymentMethod: data.paymentMethod,
        installments: data.installments,
        currentInstallment: data.currentInstallment,
      };
      const response = await api.put(`/receivables/${id}`, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('receivables');
        navigate(`/receivables/${id}`);
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar recebível:', error);
      },
    }
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.customerName) newErrors.customerName = 'Informe o cliente';
    if (!formData.category) newErrors.category = 'Selecione a categoria';
    if (!originalAmountDisplay) newErrors.originalAmount = 'Informe o valor original';
    if (!currentAmountDisplay) newErrors.currentAmount = 'Informe o saldo a pagar';
    if (!formData.dueDate) newErrors.dueDate = 'Informe o vencimento';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value } as any));
  };

  const handleCurrencyChange = (field: 'originalAmount' | 'currentAmount', rawValue: string) => {
    const masked = applyCurrencyMask(rawValue);
    if (field === 'originalAmount') {
      setOriginalAmountDisplay(masked);
    } else {
      setCurrentAmountDisplay(masked);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const data: ReceivableFormData = {
      ...formData,
      originalAmount: parseCurrency(originalAmountDisplay || '0'),
      currentAmount: parseCurrency(currentAmountDisplay || '0'),
    };
    updateMutation.mutate(data);
  };

    return (
    <FormPage
      backTo="/financeiro/contas"
      backLabel="Voltar às contas"
      breadcrumb="Financeiro / Contas / Editar"
      title="Editar conta a receber"
      subtitle="Atualize o lançamento"
    >
{/* Form */}
        <form id="edit-receivable-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
              Informações Básicas
            </h3>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente *
                </label>
                <input
                  type="text"
                  name="customerName"
                  id="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: João Silva, Empresa XYZ"
                />
                {errors.customerName && (
                  <p className="text-red-500 text-sm mt-1">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  name="category"
                  id="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="real_estate">Imóvel</option>
                  <option value="vehicle">Veículo</option>
                  <option value="mutuos">Mútuos</option>
                  <option value="other">Outros</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Descreva o recebível (ex: Venda parcelada, Contrato de prestação de serviços, etc.)"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div>
                <label htmlFor="originalAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Original *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="originalAmount"
                    id="originalAmount"
                    value={originalAmountDisplay}
                    onChange={e => handleCurrencyChange('originalAmount', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.originalAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.originalAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.originalAmount}</p>
                )}
              </div>

              <div>
                <label htmlFor="currentAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Saldo a receber *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="currentAmount"
                    id="currentAmount"
                    value={currentAmountDisplay}
                    onChange={e => handleCurrencyChange('currentAmount', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.currentAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.currentAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.currentAmount}</p>
                )}
              </div>

              <div>
                <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-2">
                  Taxa de Juros (% a.m.)
                </label>
                <input
                  type="number"
                  name="interestRate"
                  id="interestRate"
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.interestRate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.interestRate && (
                  <p className="text-red-500 text-sm mt-1">{errors.interestRate}</p>
                )}
              </div>

              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Vencimento *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.dueDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  id="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Ativo</option>
                  <option value="overdue">Em Atraso</option>
                  <option value="renegotiated">Renegociado</option>
                  <option value="paid">Pago</option>
                </select>
              </div>

              <div>
                <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                  Método de Recebimento
                </label>
                <input
                  type="text"
                  name="paymentMethod"
                  id="paymentMethod"
                  value={formData.paymentMethod || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ex: PIX, Boleto, Transferência"
                />
              </div>

              <div>
                <label htmlFor="installments" className="block text-sm font-medium text-gray-700 mb-2">
                  Parcelas
                </label>
                <input
                  type="number"
                  name="installments"
                  id="installments"
                  min={1}
                  value={formData.installments || 1}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="currentInstallment" className="block text-sm font-medium text-gray-700 mb-2">
                  Parcela Atual
                </label>
                <input
                  type="number"
                  name="currentInstallment"
                  id="currentInstallment"
                  min={1}
                  value={formData.currentInstallment || 1}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/receivables/${id}`)}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <BanknotesIcon className="h-4 w-4 mr-2" />
              Salvar Alterações
            </button>
          </div>
        </form>
          </FormPage>
  );
};

export default EditReceivable;